import React from "react";
import moment from "moment";
import { FiChevronRight } from "react-icons/fi";
import { FaCalendarDays, FaFlag } from "react-icons/fa6";
import { PiMedalLight } from "react-icons/pi";

import "./DashboardHome.css";

/*
 * Presentational home screen shared by the user & admin mobile dashboards.
 * All data / behaviour stays in the page; this only renders it.
 */
const RING_RADIUS = 51.5;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const DashboardHome = ({
  greeting,
  user,
  employeeName,
  todayAttendance,
  punchCount,
  formatTime24,
  weeklyHours,
  formatHours,
  attendanceSummary,
  startDate,
  endDate,
  upcomingHoliday,
  onHolidayClick,
}) => {
  const firstName = user?.first_name || employeeName || "-";

  const dateLabel = moment().format("ddd, DD MMM YYYY").toUpperCase();

  const required = Number(weeklyHours.total_required_hours) || 0;
  const completed = Number(weeklyHours.total_completed_hours) || 0;
  const percent =
    required > 0 ? Math.min(100, Math.round((completed / required) * 100)) : 0;
  const ringOffset = RING_CIRCUMFERENCE * (1 - percent / 100);

  const isThisMonth =
    moment(startDate).isSame(moment().startOf("month"), "day") &&
    moment(endDate).isSame(moment().endOf("month"), "day");

  const rangeLabel = isThisMonth
    ? "This Month"
    : `${moment(startDate).format("DD MMM")} - ${moment(endDate).format("DD MMM")}`;

  let statusText = "Not Checked In";
  let statusTime = null;

  if (todayAttendance.first_punch) {
    if (punchCount % 2 === 1) {
      statusText = "Checked In";
      statusTime = todayAttendance.last_punch || todayAttendance.first_punch;
    } else {
      statusText = "Checked Out";
      statusTime = todayAttendance.last_punch || todayAttendance.first_punch;
    }
  }

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div className="dh-page">
      {/* GREETING */}
      <section className="dh-greeting">
        <p className="dh-date">{dateLabel}</p>
        <p className="dh-hello">{greeting},</p>
        <div className="dh-name-row">
          <h2 className="dh-name">{firstName}!</h2>
          <span className="dh-meta">
            {user?.employee_code || "-"}
            {user?.designation_name ? `, ${user.designation_name}` : ""}
          </span>
        </div>
      </section>

      {/* HOURS */}
      <section className="dh-card dh-card-solid dh-hours">
        <div className="dh-ring">
          <svg viewBox="0 0 116 116" width="116" height="116">
            <defs>
              {/* userSpace coords: the arc is rotated -90deg, so local x maps to visual y */}
              <linearGradient
                id="dhRingGrad"
                gradientUnits="userSpaceOnUse"
                x1="116"
                y1="58"
                x2="0"
                y2="58"
              >
                <stop offset="0%" className="dh-ring-start" />
                <stop offset="100%" className="dh-ring-end" />
              </linearGradient>
            </defs>
            <circle
              cx="58"
              cy="58"
              r={RING_RADIUS}
              className="dh-ring-track"
              strokeWidth="13"
              fill="none"
            />
            <circle
              cx="58"
              cy="58"
              r={RING_RADIUS}
              stroke="url(#dhRingGrad)"
              strokeWidth="13"
              fill="none"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={ringOffset}
              transform="rotate(-90 58 58)"
            />
          </svg>
          <div className="dh-ring-center">
            <span className="dh-ring-value">{percent}%</span>
            <span className="dh-ring-label">WEEK</span>
          </div>
        </div>

        <div className="dh-vdivider dh-vdivider-red" />

        <div className="dh-hours-info">
          <p className="dh-eyebrow">HOURS LOGGED</p>
          <p className="dh-hours-main">
            <span className="dh-accent">
              {formatHours(weeklyHours.total_completed_hours)}
            </span>
            <span className="dh-hours-total">
              /{formatHours(weeklyHours.total_required_hours)}
            </span>
          </p>
          <p className="dh-hours-remaining">
            <strong>{formatHours(weeklyHours.remaining_hours)}</strong> hrs
            remaining
          </p>
          <p className="dh-cheer">
            <PiMedalLight />
            {percent >= 50 ? "You're doing great!" : "Keep going!"}
          </p>
        </div>
      </section>

      {/* TODAY'S STATUS */}
      <section className="dh-card dh-card-glass dh-status">
        <div className="dh-icon-circle">
          <span className="dh-icon-inner">
            <FaCalendarDays />
          </span>
        </div>
        <div>
          <p className="dh-status-label">Today’s Status</p>
          <p className="dh-status-value">
            {statusText}
            {statusTime && (
              <span className="dh-status-time"> at {formatTime24(statusTime)}</span>
            )}
          </p>
        </div>
      </section>

      {/* PRESENT / ABSENT */}
      <section className="dh-card dh-card-glass dh-days">
        <div className="dh-day dh-day-present">
          <span className="dh-day-value">{pad(attendanceSummary.present)}</span>
          <span className="dh-day-title">Days Present</span>
          <span className="dh-day-range">{rangeLabel}</span>
        </div>

        <div className="dh-vdivider" />

        <div className="dh-day dh-day-absent">
          <span className="dh-day-value">{pad(attendanceSummary.absent)}</span>
          <span className="dh-day-title">Days Absent</span>
          <span className="dh-day-range">{rangeLabel}</span>
        </div>
      </section>

      {/* UPCOMING HOLIDAY */}
      <section
        className="dh-card dh-card-glass dh-holiday"
        role="button"
        onClick={onHolidayClick}
      >
        <div className="dh-flag-box">
          <span className="dh-flag-inner">
            <FaFlag />
          </span>
        </div>
        <div className="dh-holiday-info">
          <p className="dh-holiday-name">
            {upcomingHoliday?.holiday_name || "No Upcoming Holiday"}
          </p>
          <p className="dh-holiday-date">
            {upcomingHoliday
              ? moment(upcomingHoliday.holiday_date).format("dddd, Do MMMM")
              : "View full list"}
          </p>
        </div>
        <span className="dh-holiday-arrow">
          <FiChevronRight />
        </span>
      </section>
    </div>
  );
};

export default DashboardHome;
